/**
 * Lease Calculator Component
 * P20250925-114 — Monthly payment calculator with term selection
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Calculator, Info } from 'lucide-react';
import { fireEvent } from '@/lib/analytics';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface LeaseCalculatorProps {
  binPrice: number;
  domain: string;
  onAddToCart?: (term: number, monthlyAmount: number) => boolean | void;
  className?: string;
  disabled?: boolean;
}

const LEASE_TERMS = [
  { months: 12, label: '12 months' },
  { months: 24, label: '24 months' },
  { months: 36, label: '36 months' },
];

// Calculate monthly lease payment
// Formula: Backend matching formula with premium-based pricing
// 12 months: 10% premium, 24 months: 15% premium, 36 months: 20% premium
function calculateMonthly(binPrice: number, months: number): number {
  let multiplier: number;
  switch (months) {
    case 12:
      multiplier = 1.10; // 10% premium
      break;
    case 24:
      multiplier = 1.15; // 15% premium
      break;
    case 36:
      multiplier = 1.20; // 20% premium
      break;
    default:
      // Fallback for any other term (shouldn't happen, but safe)
      multiplier = 1.15;
  }
  
  const totalLtoAmount = binPrice * multiplier;
  const monthlyAmount = totalLtoAmount / months;
  return Math.ceil(monthlyAmount);
}

export default function LeaseCalculator({
  binPrice,
  domain,
  onAddToCart,
  className = '',
  disabled = false,
}: LeaseCalculatorProps) {
  const [selectedTerm, setSelectedTerm] = useState(24); // Default to 24 months
  const [hasTrackedView, setHasTrackedView] = useState(false);

  const monthlyAmount = calculateMonthly(binPrice, selectedTerm);
  const totalPayments = monthlyAmount * selectedTerm;
  const totalCost = totalPayments;

  // Track calculator view on mount
  useEffect(() => {
    if (!hasTrackedView) {
      fireEvent('lease_calc_view', {
        term_default: selectedTerm,
        domain,
        bin_price: binPrice,
      });
      setHasTrackedView(true);
    }
  }, [hasTrackedView, selectedTerm, domain, binPrice]);

  const handleTermChange = (months: number) => {
    setSelectedTerm(months);
    fireEvent('lease_calc_change', {
      term: months,
      domain,
      monthly_amount: calculateMonthly(binPrice, months),
    });
  };

  const handleAddToCart = () => {
    if (onAddToCart && !disabled) {
      // Call onAddToCart which may return success/failure or void
      const result = onAddToCart(selectedTerm, monthlyAmount);
      
      // Only fire calculator-specific analytics on successful addition
      // If result is void or true, consider it success (for backward compatibility)
      // If result is explicitly false, it failed
      const success = result !== false;
      
      if (success) {
        fireEvent('lease_calc_add_to_cart', {
          term: selectedTerm,
          domain,
          monthly_amount: monthlyAmount,
          total_cost: totalCost,
          success: true,
        });
      }
    }
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Calculator className="w-5 h-5 text-primary" />
          Lease Calculator
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Term Selection */}
        <div>
          <Label className="text-sm font-medium mb-3 block">Select Lease Term</Label>
          <div className="grid grid-cols-3 gap-2">
            {LEASE_TERMS.map(({ months, label }) => (
              <Button
                key={months}
                type="button"
                variant={selectedTerm === months ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleTermChange(months)}
                className="w-full"
                aria-pressed={selectedTerm === months}
                aria-label={`Select ${label} lease term`}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>

        {/* Payment Breakdown */}
        <div className="bg-muted/50 p-4 rounded-lg space-y-2 border border-border/50">
          <div className="flex justify-between items-baseline">
            <span className="text-sm text-muted-foreground">Monthly Payment</span>
            <span className="text-2xl font-bold text-foreground">
              ${monthlyAmount.toLocaleString()}
            </span>
          </div>

          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Term Length</span>
            <span className="font-medium text-foreground">{selectedTerm} months</span>
          </div>

          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Total Payments</span>
            <span className="font-medium text-foreground">${totalPayments.toLocaleString()}</span>
          </div>

          <div className="pt-2 border-t border-border/50">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                Total Cost
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3 h-3 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="text-xs">
                      Premium-based pricing (10-20% over BIN depending on term). Option to buy at BIN price anytime during lease.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </span>
              <span className="font-semibold text-foreground">${totalCost.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="space-y-2 text-sm">
          <div className="flex items-start gap-2 text-muted-foreground">
            <span className="text-primary">✓</span>
            <span>Start using the domain immediately</span>
          </div>
          <div className="flex items-start gap-2 text-muted-foreground">
            <span className="text-primary">✓</span>
            <span>Full DNS control during lease</span>
          </div>
          <div className="flex items-start gap-2 text-muted-foreground">
            <span className="text-primary">✓</span>
            <span>Buy anytime at BIN price (${binPrice.toLocaleString()})</span>
          </div>
        </div>

        {/* CTA Button */}
        {onAddToCart && (
          <Button 
            onClick={handleAddToCart} 
            className="w-full" 
            size="lg"
            disabled={disabled}
          >
            {disabled ? 'Unavailable' : 'Add Lease to Cart'}
          </Button>
        )}

        {/* Note */}
        <p className="text-xs text-muted-foreground text-center">
          Lease terms finalized via a trusted payment provider • Secure payment processing
        </p>
      </CardContent>
    </Card>
  );
}
